import React from "react";
import Box from "@mui/material/Box";
import { colorBlindFriendlyColors } from "@seabrookstudios/pitch2table-core";

export const makePatternId = (pattern, index, color) => {
  return `pattern-${pattern}-${color.replace("#", "")}-${index}`;
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestPattern} pattern
 * @param {number} index
 * @param {string} color
 * @returns {React.ReactNode}
 */
export const makePattern = (pattern, index, color) => {
  const patternId = makePatternId(pattern, index, color);

  switch (pattern) {
    case "none":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="6" height="6">
          <rect width="6" height="6" fill={color} />
        </pattern>
      );
    case "diagonal-stripes":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill={color} />
          <path d="M-2,2 l4,-4 M2,6 l4,-4 M6,10 l4,-4" stroke="white" strokeWidth="1" opacity="0.5" />
        </pattern>
      );
    case "dots":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="6" height="6">
          <rect width="6" height="6" fill={color} />
          <circle cx="3" cy="3" r="1" fill="white" opacity="0.5" />
        </pattern>
      );
    case "horizontal-stripes":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="4">
          <rect width="8" height="4" fill={color} />
          <rect width="8" height="2" fill="white" opacity="0.5" />
        </pattern>
      );
    case "vertical-stripes":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="4" height="8">
          <rect width="4" height="8" fill={color} />
          <rect width="2" height="8" fill="white" opacity="0.5" />
        </pattern>
      );
    case "crosshatch":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="6" height="6">
          <rect width="6" height="6" fill={color} />
          <path d="M0,0 l6,6 M6,0 l-6,6" stroke="white" strokeWidth="1" opacity="0.5" />
        </pattern>
      );
    case "diamonds":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill={color} />
          <path d="M4,0 l4,4 l-4,4 l-4,-4 z" fill="white" opacity="0.5" />
        </pattern>
      );
    case "circles":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill={color} />
          <circle cx="4" cy="4" r="2" fill="white" opacity="0.5" />
        </pattern>
      );
    case "waves":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="12" height="8">
          <rect width="12" height="8" fill={color} />
          <path d="M0,4 q3,-2 6,0 q3,2 6,0" stroke="white" strokeWidth="1" fill="none" opacity="0.5" />
        </pattern>
      );
    case "zigzag":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="6">
          <rect width="8" height="6" fill={color} />
          <path d="M0,3 l2,-2 l2,2 l2,-2 l2,2" stroke="white" strokeWidth="1" fill="none" opacity="0.5" />
        </pattern>
      );
    case "grid":
      return (
        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="6" height="6">
          <rect width="6" height="6" fill={color} />
          <path d="M0,0 l6,0 M0,0 l0,6" stroke="white" strokeWidth="1" opacity="0.5" />
        </pattern>
      );
    default:
      return null;
  }
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponent} props.item
 * @param {number} [props.size]
 * @param {number} [props.patternIndex]
 */
export const ColorPatternDisplay = ({ item, size = 20, patternIndex = 0 }) => {
  const showColorAndPattern = item.type === "PART_OF_SCORE";

  if (!showColorAndPattern) {
    return null;
  }

  // Get the pattern and color for display
  const pattern = item.pattern || "none";
  const hasColour = item.colour && item.colour !== "#000000";
  const color = hasColour ? item.colour : colorBlindFriendlyColors[0]; // Use first color as fallback

  return (
    <Box width={size} height={size} display="flex" alignItems="center" justifyContent="center" lineHeight={0}>
      <svg width={size} height={size}>
        <defs>{makePattern(pattern, patternIndex, color)}</defs>
        <rect width={size} height={size} fill={`url(#${makePatternId(pattern, patternIndex, color)})`} />
      </svg>
    </Box>
  );
};
